// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// Left-hand marketing panel of the login page. Every number and photo comes from data/welcome.ts
// (which reads the site's real data) — do not hard-code claims here.
// ZP-118: panel background carries the site's deep-navy-to-bright-cyan gradient mood in dark
// mode (from-ocean-950 via-ocean-900 to-ocean-700/40); the real intro text sits top-left,
// safely inside the dark from/via zone of the diagonal, never over the lighter corner.
// ─────────────────────────────────────────────────────────────────────────────
import { Anchor, Fish, Leaf, MapPin, ShieldCheck, Timer, Waves, Zap } from "lucide-react";
import { COPY, FACTS, HERO_ITEM } from "../../data/welcome";
import { getWikimediaSrcSet } from "../../utils/wikimediaImage";

const CATCH_TAG = HERO_ITEM.tags[1] ?? HERO_ITEM.tags[0];

export default function HeroPanel({ className = "" }: { className?: string }) {
  return (
    <section
      aria-labelledby="welcome-hero-title"
      className={`flex flex-col gap-8 bg-gradient-to-br from-teal-200/70 via-foam-50 to-ocean-500/10 p-6 dark:from-ocean-950 dark:via-ocean-900 dark:to-ocean-700/40 sm:p-10 ${className}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex max-w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-full bg-white px-4 py-2.5 shadow-sm dark:bg-ocean-800">
          <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
          <span className="font-display text-xl font-bold text-teal-700 dark:text-teal-300">ZenFresh</span>
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-teal-500" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-teal-700 dark:text-teal-300">
            Fisherman direct
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-xs font-semibold text-ocean-900 shadow-sm dark:bg-ocean-800 dark:text-foam-100">
          <Leaf className="h-4 w-4 text-teal-700 dark:text-teal-300" aria-hidden="true" />
          {FACTS.traceability} Traceable Catch
        </span>
      </div>

      <div>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-teal-200/80 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-teal-700 dark:bg-teal-700/30 dark:text-teal-200">
          <Zap className="h-3.5 w-3.5" aria-hidden="true" />
          {COPY.heroBadge}
        </span>
        <h2
          id="welcome-hero-title"
          className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight text-ocean-950 dark:text-foam-50 sm:text-5xl xl:text-6xl"
        >
          {COPY.heroTitleStart}
          <br />
          <em className="italic text-teal-600 dark:text-teal-300">{COPY.heroTitleEmphasis}</em> {COPY.heroTitleEnd}
        </h2>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-ocean-800/85 dark:text-foam-100/80">{COPY.heroBody}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-[1.5fr_1fr]">
        <figure className="flex flex-col gap-2">
          <div className="relative min-h-[260px] flex-1 overflow-hidden rounded-2xl bg-ocean-900 shadow-md">
            <img
              src={HERO_ITEM.image}
              srcSet={getWikimediaSrcSet(HERO_ITEM.image)}
              alt={`${HERO_ITEM.name}, freshly landed`}
              title={HERO_ITEM.credit}
              // The largest visible image on first paint: load it early, not lazily.
              fetchPriority="high"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ocean-950/85 via-ocean-950/15 to-transparent" />
            <div className="absolute inset-x-4 bottom-4 flex items-end justify-between gap-3 text-white">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-teal-300">Today&apos;s catch</p>
                <p className="mt-1 font-display text-2xl font-bold leading-tight">{HERO_ITEM.name}</p>
              </div>
              <span className="shrink-0 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold backdrop-blur-sm">
                {CATCH_TAG}
              </span>
            </div>
          </div>
          <figcaption className="text-[11px] text-ocean-800/70 dark:text-foam-100/60">Photo: {HERO_ITEM.credit}</figcaption>
        </figure>

        <div className="flex flex-col gap-4">
          <div className="flex-1 rounded-2xl bg-white p-5 shadow-sm dark:bg-ocean-800">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-coral-500 dark:text-coral-300">
              <Timer className="h-4 w-4" aria-hidden="true" />
              Same day
            </p>
            <p className="mt-2 font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">{FACTS.avgDelivery}</p>
            <p className="text-sm text-ocean-800/80 dark:text-foam-100/70">Average, harbour to door</p>
          </div>
          <div className="flex-1 rounded-2xl bg-teal-100/80 p-5 dark:bg-teal-700/20">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-teal-700 dark:text-teal-200">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Zero middlemen
            </p>
            <p className="mt-2 font-display text-3xl font-bold text-teal-700 dark:text-teal-200">{FACTS.middlemen}</p>
            <p className="text-sm text-ocean-800/80 dark:text-foam-100/70">Straight from fishermen</p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white/75 p-4 dark:bg-ocean-800/70">
        <div className="flex items-center gap-4">
          <div aria-hidden="true" className="flex -space-x-3">
            {[Fish, Anchor, Waves].map((Icon, i) => (
              <span
                key={i}
                className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-white bg-teal-200 text-teal-700 dark:border-ocean-800"
              >
                <Icon className="h-5 w-5" />
              </span>
            ))}
            <span className="flex h-11 items-center rounded-full border-2 border-white bg-teal-700 px-3 text-sm font-bold text-white dark:border-ocean-800">
              {FACTS.partnerFishermen}
            </span>
          </div>
          <div>
            <p className="text-sm font-bold text-ocean-950 dark:text-foam-50">{FACTS.partnerFishermen} partner fishermen</p>
            <p className="text-sm text-ocean-800/80 dark:text-foam-100/70">Fresh catch landed every morning</p>
          </div>
        </div>
        <p className="flex items-center gap-2 text-sm font-semibold text-ocean-900 dark:text-foam-100">
          <MapPin className="h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
          {FACTS.liveHubs} live hubs on India&apos;s coasts
        </p>
      </div>
    </section>
  );
}
